/* iter-p3-b — Admin Support Inbox.
 *
 * Folder-based mailbox for inbound support email routed through
 * Cloudflare Email Routing → Worker → /api/webhooks/email-inbound.
 * Folders: info · complaints · requests · other.
 *
 * Left rail: folders + counts.
 * Middle rail: threads in the selected folder.
 * Right rail: selected thread messages + reply composer (uses the
 *              existing outbound email_service).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Inbox, Loader2, Send, MailOpen, FolderSymlink,
  UserRoundCog, RefreshCw, Languages, Copy, Paperclip,
  Archive, ArchiveRestore, Trash2, CheckSquare, Square, Plus, X,
  PenSquare, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { adminAuth, adminInboxApi, adminEmailTranslatorApi } from '@/lib/api';
import MailRoutingDiagnostics from './MailRoutingDiagnostics';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const FOLDER_META = [
  { id: 'all',          label: 'All',          tone: 'muted'   },
  { id: 'admin',        label: 'Admin',        tone: 'violet'  },
  { id: 'david',        label: 'David',        tone: 'indigo'  },
  { id: 'suggestions',  label: 'Suggestions',  tone: 'teal'    },
  { id: 'info',         label: 'Info',         tone: 'sky'     },
  { id: 'it',           label: 'IT',           tone: 'emerald' },
  { id: 'marketing',    label: 'Marketing',    tone: 'orange'  },
  { id: 'bookkeeping',  label: 'Bookkeeping',  tone: 'yellow'  },
  { id: 'complaints',   label: 'Complaints',   tone: 'rose'    },
  { id: 'requests',     label: 'Requests',     tone: 'amber'   },
  { id: 'spam',         label: 'Spam',         tone: 'muted'   },
  { id: 'other',        label: 'Other',        tone: 'muted'   },
];

// iter-p3-e — Sanitise every inbound HTML body before rendering.
// Inbound bodies originate from untrusted senders; without this
// strip a malicious sender could ship `<script>` / `onerror=` in
// their message and pop XSS the moment an admin opened the thread.
// Outbound HTML we generated ourselves is passed through too — it's
// cheap and keeps the rendering path identical.
const SAFE_HTML_CONFIG = {
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction'],
  ALLOW_DATA_ATTR: false,
};
function sanitizeHtml(dirty) {
  try { return DOMPurify.sanitize(dirty || '', SAFE_HTML_CONFIG); }
  catch { return ''; }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export default function AdminInboxPanel({ embedded = false } = {}) {
  const [stats, setStats]   = useState({ counts: {}, unread: 0, folders: [] });
  const [folder, setFolder] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply]     = useState('');
  const [replyFrom, setReplyFrom] = useState('');
  const [sending, setSending] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translateTo, setTranslateTo] = useState('es');
  const [languages, setLanguages] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [autoAssignOpen, setAutoAssignOpen] = useState(false);
  const [autoAssignRules, setAutoAssignRules] = useState([]);
  const [autoAssignSaving, setAutoAssignSaving] = useState(false);

  // ── Account switcher ────────────────────────────────────────────────────────
  const [accounts, setAccounts]     = useState([]);
  const [activeAccount, setActiveAccount] = useState('');   // '' = show all accounts

  // ── Compose modal ───────────────────────────────────────────────────────────
  const [composeOpen, setComposeOpen]       = useState(false);
  const [composeTo, setComposeTo]           = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody]       = useState('');
  const [composeFrom, setComposeFrom]       = useState('info@crownnrose.com');
  const [composeSending, setComposeSending] = useState(false);

  const reloadStats = useCallback(() => adminInboxApi.stats()
    .then((r) => setStats(r || { counts: {}, unread: 0 }))
    .catch(() => setStats({ counts: {}, unread: 0 })), []);

  const reloadThreads = useCallback((f = folder, s = statusFilter, acct = activeAccount) => {
    setLoading(true);
    adminInboxApi.threads(f, s, 100, acct)
      .then((r) => {
        setThreads(r?.threads || []);
        setChecked(new Set());
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load threads.'))
      .finally(() => setLoading(false));
  }, [folder, statusFilter, activeAccount]);

  const openThread = (tid) => {
    setSelectedId(tid);
    setThread(null);
    setMessages([]);
    adminInboxApi.thread(tid)
      .then((r) => {
        setThread(r?.thread || null);
        setMessages(r?.messages || []);
        setAssignee(r?.thread?.assignee || '');
        // Default reply-from to the account that received this thread.
        setReplyFrom(r?.thread?.to_addr || '');
        // Mark read best-effort.
        if (r?.thread?.unread) {
          adminInboxApi.markRead(tid).then(() => { reloadStats(); reloadThreads(); }).catch(() => {});
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load thread.'));
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && !adminAuth.user) return;
    reloadStats();
    adminInboxApi.accounts()
      .then((r) => setAccounts(r?.accounts || []))
      .catch(() => {});
    adminEmailTranslatorApi.languages()
      .then((r) => setLanguages(r?.languages || []))
      .catch(() => {});
  }, [reloadStats]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !adminAuth.user) return;
    reloadThreads(folder, statusFilter, activeAccount);
  }, [folder, statusFilter, activeAccount, reloadThreads]);

  const changeFolder = async (newF) => {
    if (!selectedId || !thread) return;
    try {
      await adminInboxApi.setFolder(selectedId, newF);
      setThread({ ...thread, folder: newF });
      toast.success(`Moved to ${newF}`);
      reloadStats(); reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not move thread.'); }
  };

  const saveAssignee = async () => {
    if (!selectedId) return;
    try {
      await adminInboxApi.assign(selectedId, assignee || null);
      toast.success(assignee ? `Assigned to ${assignee}` : 'Cleared assignment');
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign.'); }
  };

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    setSending(true);
    try {
      await adminInboxApi.reply(selectedId, { text: reply, from_account: replyFrom || undefined });
      toast.success('Reply sent');
      setReply('');
      openThread(selectedId);
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reply.'); }
    finally { setSending(false); }
  };

  const translateReply = async () => {
    if (!reply.trim()) { toast.error('Draft a reply first.'); return; }
    setTranslating(true);
    try {
      const r = await adminEmailTranslatorApi.translate({
        text: reply, target_language: translateTo, kind: 'reply',
      });
      setReply(r?.translated_text || reply);
      toast.success(`Translated to ${r?.target_label || translateTo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Translation failed.'); }
    finally { setTranslating(false); }
  };

  const copyPeer = async () => {
    if (!thread?.peer_email) return;
    try { await navigator.clipboard.writeText(thread.peer_email); toast.success('Copied'); }
    catch { /* clipboard blocked */ }
  };

  // Feb-2026 iter — archive / restore / delete single thread.
  const archiveThread = async (tid) => {
    setRowBusy({ tid, action: 'archive' });
    try {
      await adminInboxApi.archive(tid);
      toast.success('Thread archived');
      if (selectedId === tid) { setSelectedId(null); setThread(null); setMessages([]); }
      reloadStats();
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not archive.'); }
    finally { setRowBusy(null); }
  };
  const unarchiveThread = async (tid) => {
    setRowBusy({ tid, action: 'unarchive' });
    try {
      await adminInboxApi.unarchive(tid);
      toast.success('Thread restored');
      reloadStats();
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore.'); }
    finally { setRowBusy(null); }
  };
  const deleteThread = async (tid) => {
    if (!(await openConfirm({
      title: 'Permanently delete this thread?',
      description: 'Every message inside the thread is removed. This cannot be undone.',
      confirmLabel: 'Delete thread',
      tone: 'destructive',
    }))) return;
    setRowBusy({ tid, action: 'delete' });
    try {
      const r = await adminInboxApi.remove(tid);
      toast.success(`Deleted 1 thread · ${r?.messages_deleted || 0} messages`);
      if (selectedId === tid) { setSelectedId(null); setThread(null); setMessages([]); }
      reloadStats();
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete.'); }
    finally { setRowBusy(null); }
  };

  // Bulk-select helpers.
  const toggleChecked = (tid) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  };
  const toggleCheckAll = () => {
    setChecked((prev) => {
      if (prev.size === threads.length && threads.length > 0) return new Set();
      return new Set(threads.map((t) => t.id));
    });
  };
  const bulkArchive = async () => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await adminInboxApi.bulkArchive(ids);
      toast.success(`Archived ${r?.archived ?? ids.length} thread${ids.length === 1 ? '' : 's'}`);
      setChecked(new Set());
      reloadStats();
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk archive failed.'); }
    finally { setBulkBusy(false); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    if (!(await openConfirm({
      title: `Permanently delete ${ids.length} thread${ids.length === 1 ? '' : 's'}?`,
      description: 'Every message inside these threads is removed. This cannot be undone.',
      confirmLabel: `Delete ${ids.length}`,
      tone: 'destructive',
    }))) return;
    setBulkBusy(true);
    try {
      const r = await adminInboxApi.bulkDelete(ids);
      toast.success(`Deleted ${r?.threads_deleted ?? ids.length} thread(s) · ${r?.messages_deleted || 0} message(s)`);
      setChecked(new Set());
      if (selectedId && ids.includes(selectedId)) { setSelectedId(null); setThread(null); setMessages([]); }
      reloadStats();
      reloadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk delete failed.'); }
    finally { setBulkBusy(false); }
  };

  const folderCount = useMemo(() => (id) => {
    if (id === 'all') return Object.values(stats.counts || {}).reduce((a, b) => a + b, 0);
    return stats.counts?.[id] ?? 0;
  }, [stats]);

  // iter-auto-assign — load/edit/save the recipient → teammate map.
  const openAutoAssign = () => {
    adminInboxApi.getAutoAssign()
      .then((r) => {
        const m = r?.mapping || {};
        setAutoAssignRules(Object.entries(m).map(([email, name]) => ({ email, name })));
        setAutoAssignOpen(true);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load auto-assign rules.'));
  };
  const updateAutoAssignRule = (i, field, value) => {
    setAutoAssignRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };
  const removeAutoAssignRule = (i) => {
    setAutoAssignRules((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addAutoAssignRule = () => {
    setAutoAssignRules((prev) => [...prev, { email: '', name: '' }]);
  };
  const saveAutoAssign = async () => {
    const mapping = {};
    for (const r of autoAssignRules) {
      if (r.email.trim() && r.name.trim()) mapping[r.email.trim().toLowerCase()] = r.name.trim();
    }
    setAutoAssignSaving(true);
    try {
      await adminInboxApi.setAutoAssign(mapping);
      toast.success('Auto-assign rules saved');
      setAutoAssignOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save rules.'); }
    finally { setAutoAssignSaving(false); }
  };

  const sendCompose = async () => {
    if (!composeTo.trim() || !composeBody.trim()) {
      toast.error('Fill in To: and message body before sending.');
      return;
    }
    setComposeSending(true);
    try {
      await adminInboxApi.compose({
        to: composeTo.trim(),
        subject: composeSubject.trim() || '(no subject)',
        text: composeBody.trim(),
        from_account: composeFrom,
      });
      toast.success('Email sent and thread created.');
      setComposeOpen(false);
      setComposeTo(''); setComposeSubject(''); setComposeBody('');
      reloadStats();
      reloadThreads(folder, statusFilter, activeAccount);
      adminInboxApi.accounts().then((r) => setAccounts(r?.accounts || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send email.');
    } finally {
      setComposeSending(false);
    }
  };

  if (typeof window !== 'undefined' && !adminAuth.user) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className={embedded ? "bg-background text-foreground" : "h-full bg-background text-foreground"} data-testid="admin-inbox-page">
      {!embedded && (
        <header className="flex items-center justify-between border-b border-border bg-panel-header/40 px-4 py-2">
          <div className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]">
                <ArrowLeft className="h-3.5 w-3.5" /> Admin Hub
              </Button>
            </Link>
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-primary-foreground"><Inbox className="h-3.5 w-3.5" /></div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin · Email</div>
              <div className="text-[13px] font-semibold leading-tight">
                {activeAccount ? <span className="text-primary">{activeAccount}</span> : 'All Mailboxes'}
                <span className="ml-2 text-muted-foreground font-normal text-[11.5px]">{stats.unread || 0} unread</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={() => setComposeOpen(true)} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-compose-btn">
              <PenSquare className="h-3.5 w-3.5" /> Compose
            </Button>
            <Button variant="outline" size="sm" onClick={openAutoAssign} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-auto-assign-open">
              <UserRoundCog className="h-3.5 w-3.5" /> Auto-assign
            </Button>
            <Button variant="outline" size="sm" onClick={() => { reloadStats(); reloadThreads(); adminInboxApi.accounts().then((r) => setAccounts(r?.accounts || [])).catch(() => {}); }} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        </header>
      )}
      {embedded && (
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-primary-foreground"><Inbox className="h-3.5 w-3.5" /></div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin · Email</div>
              <div className="text-[13px] font-semibold leading-tight">
                {activeAccount ? <span className="text-primary text-[11.5px]">{activeAccount}</span> : 'Administrator Inbox'}
                <span className="ml-2 text-muted-foreground font-normal text-[11.5px]">{stats.unread || 0} unread</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={() => setComposeOpen(true)} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-compose-btn-embedded">
              <PenSquare className="h-3.5 w-3.5" /> Compose
            </Button>
            <Button variant="outline" size="sm" onClick={openAutoAssign} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-auto-assign-open-embedded">
              <UserRoundCog className="h-3.5 w-3.5" /> Auto-assign
            </Button>
            <Button variant="outline" size="sm" onClick={() => { reloadStats(); reloadThreads(); }} className="h-7 gap-1.5 text-[12px]" data-testid="inbox-refresh-embedded">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        </div>
      )}

      <main className="mx-auto grid max-w-[1600px] gap-3 px-3 py-4 md:grid-cols-[200px_160px_300px_1fr]">

        {/* ── Account rail ──────────────────────────────────────────── */}
        <Card className="flex flex-col gap-0.5 overflow-hidden p-2" data-testid="inbox-account-rail">
          <div className="mb-1 px-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">Mailboxes</div>
          {/* All accounts shortcut */}
          <button
            onClick={() => setActiveAccount('')}
            data-testid="inbox-account-all"
            className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-[12px] transition-colors ${!activeAccount ? 'bg-primary/10 text-foreground font-medium' : 'hover:bg-panel-header/40'}`}
          >
            <span className="flex items-center gap-1.5">
              <Inbox className="h-3 w-3 text-muted-foreground" />
              All inboxes
            </span>
            {(stats.unread || 0) > 0 && (
              <span className="rounded-full bg-primary px-1.5 font-mono-stat text-[10px] text-primary-foreground">
                {stats.unread}
              </span>
            )}
          </button>
          <div className="my-1 border-t border-border" />
          {accounts.map((acct) => (
            <button
              key={acct.address}
              onClick={() => { setActiveAccount(acct.address); setFolder('all'); setSelectedId(null); setThread(null); setMessages([]); }}
              data-testid={`inbox-account-${acct.avatar.toLowerCase()}`}
              className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px] transition-colors ${activeAccount === acct.address ? 'bg-primary/10 text-foreground font-medium' : 'hover:bg-panel-header/40'}`}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-panel-header font-mono-stat text-[9.5px] text-muted-foreground">
                {acct.avatar}
              </span>
              <span className="min-w-0 flex-1 truncate">{acct.label}</span>
              {acct.unread > 0 && (
                <span className="rounded-full bg-primary px-1.5 font-mono-stat text-[10px] text-primary-foreground">
                  {acct.unread}
                </span>
              )}
            </button>
          ))}
        </Card>

        {/* ── Folder rail ───────────────────────────────────────────── */}
        <Card className="flex flex-col gap-0.5 overflow-hidden p-2" data-testid="inbox-folder-rail">
          <div className="mb-1 px-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">Folders</div>
          {FOLDER_META.map((f) => (
            <button
              key={f.id}
              onClick={() => setFolder(f.id)}
              data-testid={`inbox-folder-${f.id}`}
              className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-[12.5px] transition-colors ${folder === f.id ? 'bg-primary/10 text-foreground' : 'hover:bg-panel-header/40'}`}
            >
              <span>{f.label}</span>
              <span className="rounded bg-panel-header px-1.5 font-mono-stat text-[10.5px] text-muted-foreground" data-testid={`inbox-count-${f.id}`}>
                {folderCount(f.id)}
              </span>
            </button>
          ))}
        </Card>

        {/* Thread list */}
        <Card className="overflow-hidden p-0" data-testid="inbox-thread-list">
          <div className="flex items-center justify-between gap-2 border-b border-border bg-panel-header/40 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <span>
              {activeAccount
                ? <><span className="text-primary">{activeAccount}</span> · {FOLDER_META.find((f) => f.id === folder)?.label}</>
                : <>Threads · {FOLDER_META.find((f) => f.id === folder)?.label}</>
              }
              {statusFilter === 'archived' && ' · Archived'}
              &nbsp;({threads.length})
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setStatusFilter((s) => (s === 'archived' ? 'open' : 'archived'))}
                data-testid="inbox-status-toggle"
                className={`rounded-sm border px-1.5 py-0.5 text-[10px] normal-case tracking-normal transition-colors ${statusFilter === 'archived' ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border bg-background hover:bg-panel-header/60'}`}
                title={statusFilter === 'archived' ? 'Back to active inbox' : 'View archived threads'}
              >
                {statusFilter === 'archived' ? 'Show active' : 'Show archived'}
              </button>
              {threads.length > 0 && (
                <button
                  type="button"
                  onClick={toggleCheckAll}
                  data-testid="inbox-check-all"
                  className="ml-1 inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 text-[10px] normal-case tracking-normal hover:bg-panel-header/60"
                  title="Toggle select-all"
                >
                  {checked.size === threads.length && threads.length > 0
                    ? <CheckSquare className="h-3 w-3" />
                    : <Square className="h-3 w-3" />}
                  All
                </button>
              )}
            </div>
          </div>
          {/* Bulk-action bar — visible whenever anything is checked. */}
          {checked.size > 0 && (
            <div
              data-testid="inbox-bulk-bar"
              className="flex items-center justify-between gap-2 border-b border-primary/40 bg-primary/10 px-3 py-1.5 text-[11.5px] text-foreground"
            >
              <span data-testid="inbox-bulk-count">
                {checked.size} selected
              </span>
              <div className="flex items-center gap-1">
                {statusFilter !== 'archived' && (
                  <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]"
                    onClick={bulkArchive} disabled={bulkBusy} data-testid="inbox-bulk-archive">
                    {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
                    Archive
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px] text-destructive"
                  onClick={bulkDelete} disabled={bulkBusy} data-testid="inbox-bulk-delete">
                  {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  Delete
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                  onClick={() => setChecked(new Set())} disabled={bulkBusy} data-testid="inbox-bulk-clear">
                  Clear
                </Button>
              </div>
            </div>
          )}
          <div className="max-h-[70vh] overflow-auto">
            {loading && <div className="p-4 text-[12px] text-muted-foreground"><Loader2 className="inline h-3.5 w-3.5 animate-spin" /> Loading…</div>}
            {!loading && threads.length === 0 && (
              <div className="p-4 text-[12px] text-muted-foreground" data-testid="inbox-thread-empty">
                {statusFilter === 'archived'
                  ? 'No archived threads in this folder yet.'
                  : 'No threads in this folder yet. Once the Cloudflare inbound worker is deployed, mail will land here automatically.'}
              </div>
            )}
            {threads.map((t) => {
              const isRowBusy = rowBusy?.tid === t.id;
              return (
              <div
                key={t.id}
                data-testid={`inbox-thread-${t.id}`}
                className={`flex items-stretch border-b border-border transition-colors hover:bg-panel-header/30 ${selectedId === t.id ? 'bg-panel-header/50' : ''}`}
              >
                <label className="flex cursor-pointer items-center px-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={checked.has(t.id)}
                    onChange={() => toggleChecked(t.id)}
                    className="h-3.5 w-3.5 accent-primary"
                    aria-label={`Select thread ${t.subject || t.peer_email}`}
                    data-testid={`inbox-thread-check-${t.id}`}
                  />
                </label>
                <button
                  onClick={() => openThread(t.id)}
                  className="min-w-0 flex-1 px-1 py-2 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate text-[12.5px] font-semibold">{t.peer_name || t.peer_email}</div>
                    <div className="shrink-0 text-[10.5px] text-muted-foreground">{fmtTime(t.last_message_at)}</div>
                  </div>
                  <div className="mt-0.5 truncate text-[11.5px] text-foreground/80">{t.subject || '(no subject)'}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                    <span className="rounded bg-panel-header px-1 py-px">{t.folder || 'other'}</span>
                    {t.assignee && <span>· {t.assignee}</span>}
                    {t.status === 'archived' && (
                      <span className="rounded bg-muted px-1 py-px text-[9.5px] uppercase tracking-wider">Archived</span>
                    )}
                    {t.unread && <span className="ml-auto rounded bg-primary px-1.5 text-primary-foreground">NEW</span>}
                  </div>
                </button>
                <div className="flex shrink-0 items-center gap-0.5 pr-2">
                  {statusFilter === 'archived' ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); unarchiveThread(t.id); }}
                      disabled={isRowBusy}
                      title="Restore to active inbox"
                      className="rounded p-1 text-muted-foreground hover:bg-panel-header/60 hover:text-foreground disabled:opacity-50"
                      data-testid={`inbox-row-unarchive-${t.id}`}
                    >
                      {isRowBusy && rowBusy?.action === 'unarchive'
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <ArchiveRestore className="h-3 w-3" />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); archiveThread(t.id); }}
                      disabled={isRowBusy}
                      title="Archive thread"
                      className="rounded p-1 text-muted-foreground hover:bg-panel-header/60 hover:text-foreground disabled:opacity-50"
                      data-testid={`inbox-row-archive-${t.id}`}
                    >
                      {isRowBusy && rowBusy?.action === 'archive'
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Archive className="h-3 w-3" />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteThread(t.id); }}
                    disabled={isRowBusy}
                    title="Delete thread"
                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    data-testid={`inbox-row-delete-${t.id}`}
                  >
                    {isRowBusy && rowBusy?.action === 'delete'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Trash2 className="h-3 w-3" />}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </Card>

        {/* Thread detail */}
        <Card className="flex flex-col overflow-hidden p-0" data-testid="inbox-thread-detail">
          {!thread ? (
            <div className="p-4 text-[12px] text-muted-foreground">Select a thread to view messages.</div>
          ) : (
            <>
              <div className="border-b border-border bg-panel-header/40 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold">{thread.subject || '(no subject)'}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="truncate">{thread.peer_name || ''} &lt;{thread.peer_email}&gt;</span>
                      <button onClick={copyPeer} className="rounded-sm border border-border px-1 py-px text-[10.5px] hover:bg-secondary" data-testid="inbox-copy-peer">
                        <Copy className="inline h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {thread.status === 'archived' ? (
                      <Button variant="outline" size="sm" onClick={() => unarchiveThread(thread.id)}
                        className="h-7 gap-1 px-2 text-[11px]" data-testid="inbox-detail-unarchive">
                        <ArchiveRestore className="h-3 w-3" /> Restore
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => archiveThread(thread.id)}
                        className="h-7 gap-1 px-2 text-[11px]" data-testid="inbox-detail-archive">
                        <Archive className="h-3 w-3" /> Archive
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => deleteThread(thread.id)}
                      className="h-7 gap-1 px-2 text-[11px] text-destructive" data-testid="inbox-detail-delete">
                      <Trash2 className="h-3 w-3" /> Delete
                    </Button>
                    <MailOpen className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground">{thread.message_count || messages.length}</span>
                  </div>
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="flex items-center gap-1.5">
                    <FolderSymlink className="h-3.5 w-3.5 text-muted-foreground" />
                    <Select value={thread.folder || 'other'} onValueChange={changeFolder}>
                      <SelectTrigger className="h-7 text-[11.5px]" data-testid="inbox-folder-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FOLDER_META.filter((f) => f.id !== 'all').map((f) => (
                          <SelectItem key={f.id} value={f.id} data-testid={`inbox-folder-opt-${f.id}`}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UserRoundCog className="h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} onBlur={saveAssignee} placeholder="Assign to teammate…" className="h-7 text-[11.5px]" data-testid="inbox-assignee" />
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-3" data-testid="inbox-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`mb-3 rounded-md border p-2.5 ${m.direction === 'outbound' ? 'border-emerald-400/40 bg-emerald-50/40' : 'border-border bg-panel-header/20'}`} data-testid={`inbox-message-${m.id}`}>
                    <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted-foreground">
                      <span>{m.direction === 'outbound' ? 'You →' : `${m.from_name || m.from_addr} →`}</span>
                      <span>{fmtTime(m.at)}</span>
                    </div>
                    {m.html ? (
                      <div className="prose prose-sm max-w-none text-[12.5px]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.html) }} />
                    ) : (
                      <div className="whitespace-pre-wrap text-[12.5px]">{m.text || '(empty body)'}</div>
                    )}
                    {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`inbox-attachments-${m.id}`}>
                        {m.attachments.map((a, i) => {
                          const href = a.url
                            ? (a.url.startsWith('http')
                                ? a.url
                                : `${process.env.REACT_APP_BACKEND_URL || ''}${a.path ? `/api/admin/inbox/attachments/${a.path}` : a.url}`)
                            : '#';
                          return (
                            <a
                              key={i}
                              href={href}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-1 text-[11px] hover:bg-secondary"
                              data-testid={`inbox-attachment-${m.id}-${i}`}
                            >
                              <Paperclip className="h-3 w-3" />
                              {a.filename}
                              {a.size ? <span className="text-muted-foreground">({Math.round(a.size / 1024)}KB)</span> : null}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="border-t border-border p-3">
                <div className="mb-1.5 flex items-center gap-2">
                  <Label className="text-[11.5px] text-muted-foreground shrink-0">From:</Label>
                  <select
                    value={replyFrom}
                    onChange={(e) => setReplyFrom(e.target.value)}
                    className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="inbox-reply-from"
                  >
                    {accounts.length === 0 && <option value="">Default sender</option>}
                    {accounts.map((a) => (
                      <option key={a.address} value={a.address}>{a.name ? `${a.name} <${a.address}>` : a.address}</option>
                    ))}
                  </select>
                </div>
                <Label className="text-[11.5px]">Reply</Label>
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply…"
                  className="mt-1 min-h-[100px] text-[12.5px]"
                  data-testid="inbox-reply-text"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <Languages className="h-3.5 w-3.5 text-muted-foreground" />
                    <Select value={translateTo} onValueChange={setTranslateTo}>
                      <SelectTrigger className="h-7 w-32 text-[11.5px]" data-testid="inbox-translate-target"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(languages.length ? languages : [{ code: 'es', label: 'Spanish' }, { code: 'ar', label: 'Arabic' }, { code: 'zh', label: 'Chinese' }])
                          .map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={translateReply} disabled={translating || !reply.trim()} className="h-7 gap-1 text-[11.5px]" data-testid="inbox-translate">
                      {translating ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                      Translate draft
                    </Button>
                  </div>
                  <Button onClick={sendReply} disabled={sending || !reply.trim()} className="ml-auto gap-1.5" data-testid="inbox-send">
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Send reply
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </main>

      {/* iter-auto-assign — recipient → teammate rules dialog. */}
      <Dialog open={autoAssignOpen} onOpenChange={setAutoAssignOpen}>
        <DialogContent className="max-w-md" data-testid="inbox-auto-assign-dialog">
          <DialogHeader>
            <DialogTitle>Auto-assign rules</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">
            Mail to a shared address automatically tags the thread for that
            teammate (everyone still sees it — this is just a label).
          </p>
          <div className="max-h-[320px] space-y-2 overflow-auto">
            {autoAssignRules.map((r, i) => (
              <div key={i} className="flex items-center gap-1.5" data-testid={`auto-assign-rule-${i}`}>
                <Input
                  value={r.email}
                  onChange={(e) => updateAutoAssignRule(i, 'email', e.target.value)}
                  placeholder="d.willis@crownnrose.com"
                  className="h-8 text-[12px]"
                  data-testid={`auto-assign-rule-${i}-email`}
                />
                <Input
                  value={r.name}
                  onChange={(e) => updateAutoAssignRule(i, 'name', e.target.value)}
                  placeholder="David"
                  className="h-8 w-32 text-[12px]"
                  data-testid={`auto-assign-rule-${i}-name`}
                />
                <button
                  type="button"
                  onClick={() => removeAutoAssignRule(i)}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  data-testid={`auto-assign-rule-${i}-remove`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {autoAssignRules.length === 0 && (
              <div className="text-[12px] text-muted-foreground">No rules yet.</div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={addAutoAssignRule} className="gap-1.5 text-[12px]" data-testid="auto-assign-rule-add">
            <Plus className="h-3.5 w-3.5" /> Add rule
          </Button>
          <DialogFooter>
            <Button onClick={saveAutoAssign} disabled={autoAssignSaving} data-testid="auto-assign-save">
              {autoAssignSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save rules
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Compose modal ─────────────────────────────────────────────── */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[14px]">
              <PenSquare className="h-4 w-4" /> New Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* From */}
            <div className="flex items-center gap-2">
              <span className="w-14 shrink-0 text-right text-[12px] text-muted-foreground">From</span>
              <select
                value={composeFrom}
                onChange={(e) => setComposeFrom(e.target.value)}
                className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="compose-from"
              >
                {[
                  { address: 'd.willis@crownnrose.com',     label: 'David Willis' },
                  { address: 'admin@crownnrose.com',         label: 'Admin' },
                  { address: 'it@crownnrose.com',            label: 'IT Services' },
                  { address: 'marketing@crownnrose.com',     label: 'Marketing' },
                  { address: 'bookkeeping@crownnrose.com',   label: 'Bookkeeping' },
                  { address: 'suggestions@crownnrose.com',   label: 'Suggestions' },
                  { address: 'info@crownnrose.com',          label: 'Info / Support' },
                ].map((a) => (
                  <option key={a.address} value={a.address}>
                    {a.label} &lt;{a.address}&gt;
                  </option>
                ))}
              </select>
            </div>
            {/* To */}
            <div className="flex items-center gap-2">
              <span className="w-14 shrink-0 text-right text-[12px] text-muted-foreground">To</span>
              <input
                type="email"
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                placeholder="recipient@example.com"
                className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-[12px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="compose-to"
              />
            </div>
            {/* Subject */}
            <div className="flex items-center gap-2">
              <span className="w-14 shrink-0 text-right text-[12px] text-muted-foreground">Subject</span>
              <input
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="(optional)"
                className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-[12px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="compose-subject"
              />
            </div>
            {/* Body */}
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              rows={10}
              placeholder="Message…"
              className="w-full resize-y rounded-md border border-border bg-background p-2 text-[12.5px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="compose-body"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} data-testid="compose-cancel">Cancel</Button>
            <Button onClick={sendCompose} disabled={composeSending || !composeTo.trim() || !composeBody.trim()} data-testid="compose-send">
              {composeSending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
